import { useEffect, useRef, useState } from 'react';
import close_img from 'assets/close.svg?url';
import styled from 'styled-components';

import { useSoundEnabled } from '../../hooks/useSoundEnabled';

interface VideoModalProps {
  pictureUrl: string;
  videoUrl: string;
  onClose?: () => void;
  zIndex?: number;
  canClose?: boolean;
}

export default function VideoModal({
  pictureUrl,
  videoUrl,
  onClose = () => { },
  zIndex = 200,
  canClose = true
}: VideoModalProps): JSX.Element | null {
  const [isPlaying, setIsPlaying] = useState<boolean>(true);
  const [pause, setPause] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { isSoundEnabled } = useSoundEnabled();

  const handleKeyPress = (evt: KeyboardEvent): void => {
    if (evt.keyCode === 27 && canClose) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  useEffect(() => {
    if (videoRef.current) {
      if (pause === false) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, [pause]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = !isSoundEnabled;
    }
  }, [isSoundEnabled]);

  if (!videoUrl || !pictureUrl) return null;

  return (
    <Wrapper zIndex={zIndex} onClick={onClose}>
      {canClose && (
        <TopBox>
          <CloseIcon alt={'close'} src={close_img} onClick={onClose} />
        </TopBox>
      )}
      <Box onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <ContentPlaceholder>
          {isPlaying ? (
            <Video
              ref={videoRef}
              autoPlay={true}
              muted={!isSoundEnabled}
              onClick={() => setPause(!pause)}
              onEnded={() => setIsPlaying(false)}
            >
              <source src={videoUrl} type="video/mp4" />
            </Video>
          ) : (
            <Image alt={'picture'} src={pictureUrl} onClick={() => setIsPlaying(true)} />
          )}
        </ContentPlaceholder>
      </Box>
    </Wrapper>
  );
}

interface WrapperProps {
  zIndex?: number;
  children: React.ReactNode;
  onClick: () => void;
}

interface BoxProps {
  onClick: (e: React.MouseEvent) => void;
  children: React.ReactNode;
}

interface CloseIconProps {
  alt: string;
  src: string;
  onClick: () => void;
}

interface VideoProps {
  autoPlay: boolean;
  onClick: () => void;
  onEnded: () => void;
  ref: React.RefObject<HTMLVideoElement>;
  children: React.ReactNode;
  muted: boolean;
}

interface ImageProps {
  alt: string;
  src: string;
  onClick: () => void;
}

const Wrapper = styled.div<WrapperProps>`
  position: fixed;
  z-index: ${props => (props.zIndex === undefined ? 10 : props.zIndex)};
  top: 0px;
  bottom: 0px;
  left: 0px;
  right: 0px;
  background: rgba(51, 51, 51, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow: hidden;
  justify-content: center;
  cursor: pointer;
`;

const Box = styled.div<BoxProps>`
  box-sizing: border-box;
  color: white;
  border-radius: 12px;
  cursor: default;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  max-width: min(calc(100vw - 48px), calc(100vh - 48px));
  max-height: min(calc(100vw - 48px), calc(100vh - 48px));
  width: fit-content;
  height: fit-content;
`;

const Video = styled.video<VideoProps>`
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  cursor: pointer;
`;

const Image = styled.img<ImageProps>`
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  cursor: pointer;
`;

const TopBox = styled.div`
  position: absolute;
  right: 24px;
  top: 24px;
  z-index: 1;
`;

const CloseIcon = styled.img<CloseIconProps>`
  width: 32px;
  height: 32px;
  cursor: pointer;
  
  @media (max-width: 720px) {
    z-index: 11;
  }
`;

const ContentPlaceholder = styled.div`
  font-family: Nunito Sans !important;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;